import type { EpcProjectMembershipRow, EpcProjectTeamRole } from '../../../../services/epcProjectService';

export const GOVERNANCE_ROLES: EpcProjectTeamRole[] = [
  'epc_head',
  'pmo',
  'project_head',
  'project_coordinator',
];

export function isGovernanceRole(role: EpcProjectTeamRole): boolean {
  return GOVERNANCE_ROLES.includes(role);
}

export type TeamCapability =
  | 'governance_full'
  | 'dept_head'
  | 'manager'
  | 'bootstrap'
  | 'viewer'
  | 'outsider';

export function resolveTeamCapability(
  isSuperAdmin: boolean,
  hasEpcProjectUpdate: boolean,
  myUserId: string | undefined,
  memberships: EpcProjectMembershipRow[]
): { cap: TeamCapability; mine?: EpcProjectMembershipRow } {
  if (isSuperAdmin) return { cap: 'governance_full' };
  const mine = myUserId ? memberships.find((m) => m.userId === myUserId) : undefined;
  if (mine && isGovernanceRole(mine.role)) return { cap: 'governance_full', mine };
  if (!mine && memberships.length === 0 && hasEpcProjectUpdate) return { cap: 'bootstrap' };
  if (mine?.role === 'department_head') return { cap: 'dept_head', mine };
  if (mine?.role === 'manager') return { cap: 'manager', mine };
  if (mine) return { cap: 'viewer', mine };
  return { cap: 'outsider' };
}

export function governanceMembers(memberships: EpcProjectMembershipRow[]): EpcProjectMembershipRow[] {
  return memberships.filter((m) => isGovernanceRole(m.role));
}

export function canDeactivateTarget(
  cap: TeamCapability,
  isSuperAdmin: boolean,
  myUserId: string | undefined,
  mine: EpcProjectMembershipRow | undefined,
  target: EpcProjectMembershipRow
): boolean {
  if (target.inactive) return false;
  if (isSuperAdmin || cap === 'governance_full') return true;
  if (cap === 'manager' && myUserId) {
    return target.role === 'site_engineer' && target.reportsToUserId === myUserId;
  }
  if (cap === 'dept_head' && mine?.orgUnitId && target.orgUnitId === mine.orgUnitId) {
    return target.role === 'manager';
  }
  return false;
}

export const ROLE_LABEL: Record<EpcProjectTeamRole, string> = {
  epc_head: 'EPC head',
  pmo: 'PMO',
  project_head: 'Project head',
  project_coordinator: 'Project coordinator',
  department_head: 'Department head',
  manager: 'Manager',
  site_engineer: 'Site engineer',
};
